'use client';

import * as Sentry from '@sentry/nextjs';
import { CustomFormApi, CustomFormState, Form, useFormState } from 'informed';
import {
  FC,
  memo,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import { useTranslation } from 'react-i18next';
import { useShallow } from 'zustand/react/shallow';

import { useErrorValidation } from '../hooks/useErrorValidation';
import { RegistrationPromoInput } from './PromoInput';
import { RegistrationFormCheckboxList } from './RegistrationFormCheckboxList';
import { AnalyticsEventName } from '@/api/analytics';
import { register, RegisterForm, User } from '@/api/auth';
import { Button } from '@/components/buttons';
import { ContinueWithGoogle } from '@/components/ContinueWithGoogle';
import { FormGroup } from '@/components/form/groups/FormGroup';
import { InputEmail } from '@/components/form/InputEmail';
import { Password } from '@/components/form/Password';
import { Select } from '@/components/form/Select';
import { NamesSteps } from '@/helpers/auth';
import { getCountryFlagIcon } from '@/helpers/cdn';
import {
  getCurrencyCodeByLocation,
  isCryptoCurrency,
} from '@/helpers/currency';
import { ThemeMode } from '@/helpers/enums';
import { Modals } from '@/helpers/modals';
import { showTooltip } from '@/helpers/tooltip';
import { required, validatePassword } from '@/helpers/validators';
import { useAnalytics } from '@/hooks/analytics';
import { useLogin } from '@/hooks/login';
import { useNavigation } from '@/hooks/navigation';
import { useSwrMutation } from '@/hooks/swr';
import { useIsLanding } from '@/hooks/useIsLanding';
import { useNDBFlow } from '@/hooks/useNDBFlow';
import { useGoogleRegistrationABTest } from '@/modules/common';
import { PersistentFormTracker } from '@/modules/forms/components/PersistentFormTracker';
import {
  sendRegFormAppearanceEvent,
  sendRegFormFirstTouchedEvent,
  sendRegFormSubmitEvent,
} from '@/modules/googleAnalytics';
import { showImmidiatellyAdditionalDataStepRequiredABTest } from '@/modules/lobby/components/RegistrationSlider/helpers';
import { useServer } from '@/modules/server/context/ServerEnvContext';
import {
  STORAGE,
  useCommonStore,
  useLocalStorageStore,
  useModalsStore,
} from '@/store';
import { REASON_FOR_UPDATING_USER } from '@/store/commonStore/slices/auth/helpers';
import {
  PersistentFormName,
  usePersistentFormsSlice,
} from '@/store/commonStore/slices/persistant/usePersistentFormsSlice';
import { TooltipTypes } from '@/store/tooltipStore';

import { ScRegistrationFormRow, ScRegistrationWrapButtons } from '../styled';
import { ScFormRegistration } from '@/components/form/groups/styled';

export interface RegistrationFormProps {
  formId?: string;
  isOneStepRegistration?: boolean;
  openInnerModalsInPortal?: boolean;
  isInputPromoStag?: boolean;
  handleChange?: (formData: CustomFormState<RegisterForm>) => void;
  isSliderShortForm?: boolean;
  isFromDeposit?: boolean;
}

export const RegistrationForm: FC<RegistrationFormProps> = ({
  formId,
  isOneStepRegistration,
  openInnerModalsInPortal,
  isInputPromoStag,
  handleChange,
  isSliderShortForm,
}) => {
  // TODO: refactor
  const { navigate } = useNavigation();
  const { sendEventToAnalytics } = useAnalytics();
  const { t } = useTranslation('common');
  const close = useModalsStore.use.close();
  const {
    setCurrentPersistentStep,
    getPersistentFormData,
    resetPersistentForm,
  } = usePersistentFormsSlice();
  const initialValues = getPersistentFormData(
    PersistentFormName.PreRegistration
  ) as RegisterForm;

  const [showPromosCheckboxes, setShowPromosCheckboxes] = useState(
    initialValues?.receivePromosMain || false
  );
  const { isLanding } = useIsLanding();
  const { cmsABTests, countryCode } = useServer();

  const {
    afterRegistrationRedirectUrl,
    countries,
    enableCurrencies,
    pendingUser,
    currencies,
    regBannerActionBtnRect,
    fetchUser,
    insertUser,
    initialRegFormCurrency,
  } = useCommonStore(
    useShallow((state) => ({
      afterRegistrationRedirectUrl: state.afterRegistrationRedirectUrl,
      countries: state.countries,
      enableCurrencies: state.enableCurrencies,
      pendingUser: state.pendingUser,
      currencies: state.currencies,
      regBannerActionBtnRect: state.regBannerActionBtnRect,
      fetchUser: state.fetchUser,
      insertUser: state.insertUser,
      initialRegFormCurrency: state.initialRegFormCurrency,
    }))
  );

  const { isNDBFlow } = useNDBFlow();

  const open = useModalsStore.use.open();
  const {
    trigger: requestRegister,
    error: registerError,
    isMutating: registerPending,
  } = useSwrMutation<User>({
    key: 'registerKey',
    fetcher: register,
    options: { populateCache: true },
  });
  const { handleLogin, loginPending, userData, loginError } = useLogin();
  const [isAuthRequestsPending, setIsAuthRequestsPending] = useState(false);
  const formApiRef = useRef<CustomFormApi<RegisterForm>>();
  useErrorValidation({
    initialValues,
    registerError,
    loginError,
    isAuthRequestsPending,
    setIsAuthRequestsPending,
    isOneStepRegistration,
    formApiRef,
  });

  const landingDepositNavigate = useCallback(() => {
    useCommonStore.setState({
      regBanner: null,
      initialRegFormCurrency: null,
    });
    navigate(isNDBFlow ? '/?modal=ndb-email-confirm' : '/?modal=deposit');
  }, [navigate, isNDBFlow]);

  const handleSubmit = useCallback(
    async ({ termsAcceptanceAgeAcceptance, ...values }: RegisterForm) => {
      delete values.receivePromosMain;

      // TODO: remove this when the event is added to the google analytics
      if (isOneStepRegistration) {
        sendEventToAnalytics({
          name: AnalyticsEventName.LOBBY__REGISTRATION__FORM__SUBMIT,
          attributes: {
            email: values.email,
          },
        });
      } else {
        sendEventToAnalytics({
          name: AnalyticsEventName.MODAL__REGISTRATION__FORM__SUBMIT,
          attributes: {
            email: values.email,
          },
        });
      }

      setIsAuthRequestsPending(true);

      const loginValues = {
        email: values.email || initialValues.email,
        password: values.password || initialValues.password,
      };

      const receivePromos = !(
        values.receivePromos || initialValues.receivePromos
      );
      const receivePromosViaPhoneCalls = !(
        values.receivePromosViaPhoneCalls ||
        initialValues.receivePromosViaPhoneCalls
      );
      const receiveSmsPromos = !(
        values.receiveSmsPromos || initialValues.receiveSmsPromos
      );

      const requestData = {
        ...values,
        ...loginValues,
        receivePromos,
        receiveSmsPromos,
        ageAcceptance:
          termsAcceptanceAgeAcceptance ||
          initialValues.termsAcceptanceAgeAcceptance,
        termsAcceptance:
          termsAcceptanceAgeAcceptance ||
          initialValues.termsAcceptanceAgeAcceptance,
        receivePromosViaPhoneCalls,
        agreedToPartnerPromotions: !!(
          values.agreedToPartnerPromotions ||
          initialValues.agreedToPartnerPromotions
        ),
      };
      try {
        const response = await requestRegister(requestData);
        if (response) {
          resetPersistentForm(PersistentFormName.PreRegistration);

          sendRegFormSubmitEvent({
            status: 'success',
            action: 'registration',
            method: 'email',
          });

          // TODO: remove this when the event is added to the google analytics
          if (isOneStepRegistration) {
            await sendEventToAnalytics({
              name: AnalyticsEventName.LOBBY__REGISTRATION__FORM__SUCCESS,
            });
          } else {
            await sendEventToAnalytics({
              name: AnalyticsEventName.MODAL__REGISTRATION__FORM__SUCCESS,
            });

            showTooltip({
              type: TooltipTypes.SUCCESS,
              body: t('auth.registration.success'),
            });
          }

          const isCrypto = isCryptoCurrency(currencies, values.currency);

          if (!isOneStepRegistration && !isCrypto) {
            setCurrentPersistentStep(
              PersistentFormName.Registration,
              NamesSteps.PERSONAL_INFORMATION
            );
          }

          // TODO: order this function is required until fix additional modal
          await fetchUser(REASON_FOR_UPDATING_USER.REGISTRATION);

          if (isSliderShortForm) {
            const isImmidiatellyAdditionalDataStepRequired =
              showImmidiatellyAdditionalDataStepRequiredABTest(cmsABTests);

            if (isImmidiatellyAdditionalDataStepRequired) {
              if (isCrypto) {
                open(Modals.Deposit);
              } else {
                setCurrentPersistentStep(
                  PersistentFormName.Registration,
                  NamesSteps.PERSONAL_INFORMATION
                );
                open(Modals.Registration);
                showTooltip({
                  type: TooltipTypes.SUCCESS,
                  body: t('auth.registration.success'),
                });
              }
            }
          }

          if (!isOneStepRegistration && isCrypto) {
            if (afterRegistrationRedirectUrl) {
              return navigate(afterRegistrationRedirectUrl);
            }
            if (isLanding) {
              return landingDepositNavigate();
            }

            return isNDBFlow
              ? open(Modals.NDBEmailConfirm)
              : open(Modals.Deposit);
          }

          if (isOneStepRegistration && isLanding) {
            landingDepositNavigate();
          }
        }
      } catch (e: any) {
        if (e.email?.taken) {
          handleLogin(loginValues);
        } else {
          Sentry.captureMessage(
            `Error in registration request - ${JSON.stringify(e)}`
          );
          setIsAuthRequestsPending(false);
          showTooltip({
            type: TooltipTypes.ERROR,
            body: t('errorUnknown'),
          });
          sendRegFormSubmitEvent({
            status: 'failed',
            action: 'registration',
            method: 'email',
            request_data: requestData,
            error_data: e,
          });
        }
      }
    },
    [
      afterRegistrationRedirectUrl,
      isOneStepRegistration,
      requestRegister,
      sendEventToAnalytics,
      fetchUser,
      currencies,
      landingDepositNavigate,
      open,
      t,
      isLanding,
      handleLogin,
    ]
  );

  const currenciesOptions = useMemo(
    () =>
      enableCurrencies
        .filter(({ isDisabledOnRegistration }) => !isDisabledOnRegistration)
        .map(({ code, symbol, fiat }) => ({
          name: fiat ? code : symbol,
          value: code,
        })),
    [enableCurrencies]
  );

  const preselectedValues = {
    country: countryCode,
    currency: getCurrencyCodeByLocation(countryCode),
    termsAcceptanceAgeAcceptance: false,
  };

  const isEventTouchedSent = useRef(false);
  const saveFormValuesOnChange = useCallback(
    ({ values, touched }: CustomFormState<RegisterForm>) => {
      const isFormTouched = Object.values(touched).some((t) => t);

      if (isFormTouched) {
        if (!isEventTouchedSent.current) {
          sendRegFormFirstTouchedEvent();
          isEventTouchedSent.current = true;
        }
      }
    },
    []
  );

  const handlePromoCheckboxesOnChange = useCallback(
    (formState: CustomFormState<RegisterForm>) => {
      const receivePromosMainCheckbox = formState.values?.receivePromosMain;

      handleChange && handleChange(formState);

      setShowPromosCheckboxes(!!receivePromosMainCheckbox);
    },
    [handleChange]
  );

  const isGoogleRegAvailable = useGoogleRegistrationABTest();

  const countriesOptions = useMemo(() => {
    return countries.map(({ name, code }) => ({
      name,
      value: code,
      icon: getCountryFlagIcon(code.toLowerCase()),
    }));
  }, [countries]);

  useEffect(() => {
    sendRegFormAppearanceEvent();
  }, []);

  useEffect(() => {
    if (userData) {
      insertUser(userData, REASON_FOR_UPDATING_USER.LOGIN);
      setIsAuthRequestsPending(false);
      sendRegFormSubmitEvent({
        status: 'success',
        action: 'login',
        method: 'email',
      });
      close();
      isLanding && navigate('/');
      return;
    }
  }, [userData, insertUser, close, navigate, isLanding]);

  useEffect(() => {
    handleChange &&
      formApiRef.current &&
      handleChange(formApiRef.current?.getFormState());
  }, [handleChange]);

  useEffect(() => {
    if (!showPromosCheckboxes) {
      formApiRef.current?.setValue('receivePromos', false);
      formApiRef.current?.setValue('receiveSmsPromos', false);
      formApiRef.current?.setValue('receivePromosViaPhoneCalls', false);
    }
  }, [showPromosCheckboxes]);

  return (
    <>
      {isGoogleRegAvailable && (
        <ContinueWithGoogle
          dividerGap="small"
          marginBottom={isOneStepRegistration ? 'small' : 'large'}
          analyticsEventName={
            AnalyticsEventName.MODAL__REGISTRATION__GOOGLE_AUTH_BUTTON_CLICK
          }
        />
      )}

      <Form<RegisterForm>
        id={formId}
        formApiRef={formApiRef}
        onSubmit={({ values }) => handleSubmit(values)}
        onChange={(formData: CustomFormState<RegisterForm>) => {
          saveFormValuesOnChange(formData);
          handlePromoCheckboxesOnChange(formData);
        }}
        initialValues={{ ...preselectedValues, ...initialValues }}
      >
        <MemoizedAnalytics isOneStepRegistration={!!isOneStepRegistration} />
        <PersistentFormTracker formName={PersistentFormName.PreRegistration} />
        <ScFormRegistration>
          <FormGroup title={t('auth.registration.form.email')}>
            <InputEmail />
          </FormGroup>
          <FormGroup title={t('auth.registration.form.password')}>
            <Password
              name="password"
              validate={validatePassword}
              validateOn="change"
              strengthChecker
            />
          </FormGroup>
          <ScRegistrationFormRow>
            <FormGroup title={t('common:auth.registration.form.country')}>
              <Select
                name="country"
                placeholder={t('common:auth.registration.form.country')}
                validate={required}
                options={countriesOptions}
                mode={ThemeMode.Light}
              />
            </FormGroup>
            <FormGroup title={t('auth.registration.form.currency')}>
              <Select
                name="currency"
                placeholder={t('auth.registration.form.selectCurrency')}
                options={currenciesOptions}
                validate={required}
                cols={1}
                openOptionsInPortal={openInnerModalsInPortal}
                mode={ThemeMode.Light}
                initialValue={initialRegFormCurrency || null}
              />
            </FormGroup>
          </ScRegistrationFormRow>
          <FormGroup marginTop={32}>
            <div className="checkbox-list">
              <RegistrationFormCheckboxList
                formId={formId}
                showPromosCheckboxes={showPromosCheckboxes}
                openInnerModalsInPortal={openInnerModalsInPortal}
              />
              {!isOneStepRegistration && isInputPromoStag && (
                <FormGroup title={t('havePromoCode')}>
                  <RegistrationPromoInput />
                </FormGroup>
              )}
            </div>
          </FormGroup>
        </ScFormRegistration>
        {isOneStepRegistration ? (
          <div
            className="reg-banner-btn"
            ref={(node) => {
              if (!node || regBannerActionBtnRect) return;
              useCommonStore.setState({
                regBannerActionBtnRect: node.getBoundingClientRect(),
              });
            }}
          >
            <Button
              primary
              small
              wide
              type="submit"
              isLoading={registerPending || loginPending}
            >
              {t('button.joinNow')}
            </Button>
          </div>
        ) : (
          <ScRegistrationWrapButtons cols={1}>
            <div className="right-col">
              <Button
                primary
                large
                type="submit"
                isLoading={pendingUser || registerPending || loginPending}
                wide
              >
                {t('auth.registration.buttons.next')}
              </Button>
            </div>
          </ScRegistrationWrapButtons>
        )}
      </Form>
    </>
  );
};

// TODO: remove this when the event is added to the google analytics
const Analytics: FC<{ isOneStepRegistration: boolean }> = ({
  isOneStepRegistration,
}) => {
  const { sendEventToAnalytics } = useAnalytics();

  const { touched, errors } = useFormState();

  const errorsJSON = useMemo(() => {
    try {
      return JSON.stringify(errors);
    } catch {
      return 'Error occured, but is not valid JSON';
    }
  }, [errors]);

  const touchedForm = Object.values(touched).some((t) => t);

  useEffect(() => {
    !isOneStepRegistration &&
      sendEventToAnalytics({
        name: AnalyticsEventName.MODAL__REGISTRATION__FORM__APPEARANCE,
      });
  }, [isOneStepRegistration, sendEventToAnalytics]);

  useEffect(() => {
    if (errorsJSON !== '{}') {
      const attributes = { additionalData: errorsJSON };

      isOneStepRegistration
        ? sendEventToAnalytics({
            name: AnalyticsEventName.LOBBY__REGISTRATION__FORM__ERROR,
            attributes,
          })
        : sendEventToAnalytics({
            name: AnalyticsEventName.MODAL__REGISTRATION__FORM__ERROR,
            attributes,
          });
    }
  }, [errorsJSON, sendEventToAnalytics, isOneStepRegistration]);

  useEffect(() => {
    if (touchedForm) {
      isOneStepRegistration
        ? sendEventToAnalytics({
            name: AnalyticsEventName.LOBBY__REGISTRATION__FORM__TOUCHED,
          })
        : sendEventToAnalytics({
            name: AnalyticsEventName.MODAL__REGISTRATION__FORM__TOUCHED,
          });
    }
  }, [touchedForm, sendEventToAnalytics, isOneStepRegistration]);

  return null;
};

const MemoizedAnalytics = memo(Analytics);
